import React, { useState } from "react";
import { createPortal } from "react-dom";
import DataService from "../services/data.service";
import { renderBatteryCompact } from "./mySystemsFormRenderers/battery";
import { renderLamperSixLines } from "./mySystemsFormRenderers/lamps";
import { renderValueSafeInline } from "./mySystemsFormRenderers/valueFormat";
import { StatusIcon } from "./mySystemsFormRenderers/statusIcon";
import {
  formatNorwegianShort,
  isOlderThanTwoHours,
} from "./mySystemsFormRenderers/dateFormat";

const STATUS_COLOR = {
  AG: "#2ecc71",
  AY: "#f1c40f",
  AR: "#e74c3c",
};

const getStatusColor = (status) => STATUS_COLOR[status] ?? "#bdc3c7";

const getMenuPosition = (event, width = 300, height = 260) => {
  const x =
    event.clientX + width > window.innerWidth - 12
      ? event.clientX - width
      : event.clientX;
  const y =
    event.clientY + height > window.innerHeight - 12
      ? event.clientY - height
      : event.clientY;

  return {
    x: Math.max(12, x),
    y: Math.max(12, y),
  };
};

export default function ClusterSystemsCard({
  cluster,
  activeLogg,
  userIsModerator,
  onLoggUpdated,
}) {
  const [menu, setMenu] = useState(null);
  const [editingMode, setEditingMode] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [overrideDraft, setOverrideDraft] = useState("");

  const showBattery =
    Array.isArray(cluster.systems) &&
    cluster.systems.some((s) => {
      const b = s?.batteri ?? s?.battery ?? s?.batt ?? null;
      return (
        b != null &&
        ((Array.isArray(b) && b.length > 0) ||
          (typeof b === "object" && Object.keys(b).length > 0))
      );
    });

  const closeMenu = () => {
    setMenu(null);
    setEditingMode(null);
  };

  const startEdit = () => {
    if (!menu?.activeComment) return;
    setCommentDraft(menu.activeComment.comment ?? "");
    setOverrideDraft(menu.activeComment.overrideStatus ?? "");
    setEditingMode("edit");
  };

  const startCreate = () => {
    setCommentDraft("");
    setOverrideDraft("");
    setEditingMode("create");
  };

  const archiveActive = async () => {
    if (!menu?.activeComment) return;

    try {
      await DataService.updateLogg({
        id: menu.activeComment.id,
        systemId: menu.system.id,
        active: false,
      });
      closeMenu();
      if (onLoggUpdated) onLoggUpdated();
    } catch (error) {
      alert("Kunne ikke arkivere aktiv kommentar.");
    }
  };

  const saveComment = async () => {
    if (!menu || !editingMode) return;

    const comment = commentDraft.trim();
    const overrideStatus = overrideDraft || null;
    const payload =
      editingMode === "edit" && menu.activeComment
        ? {
            id: menu.activeComment.id,
            systemId: menu.system.id,
            comment,
            overrideStatus,
          }
        : {
            systemId: menu.system.id,
            active: true,
            comment,
            overrideStatus,
          };

    try {
      await DataService.updateLogg(payload);
      closeMenu();
      if (onLoggUpdated) onLoggUpdated();
    } catch (error) {
      alert("Kunne ikke lagre kommentar / status.");
    }
  };

  return (
    <div className="viewcard21" onClick={closeMenu}>
      <h2 style={{ marginBottom: 4 }}>{cluster.nick}</h2>
      <h5 style={{ marginTop: 0, marginBottom: 8 }}>{cluster.name}</h5>

      {Array.isArray(cluster.systems) && cluster.systems.length > 0 ? (
        <table className="systems-table" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col className="status-col" />
            <col className="system-col" />
            <col className="lamper-col" />
            <col className="ctrl-col" />
            {showBattery ? <col className="battery-col" /> : null}
          </colgroup>

          <thead>
            <tr>
              <th>Status</th>
              <th>Merde</th>
              <th>Lamper</th>
              <th>Kontrollboks</th>
              {showBattery ? <th>Batteri</th> : null}
            </tr>
          </thead>

          <tbody>
            {cluster.systems.map((sys, idx) => {
              const updated = formatNorwegianShort(sys?.updatedAt);
              const old = isOlderThanTwoHours(sys?.updatedAt);
              const batteryVal =
                sys?.batteri ?? sys?.battery ?? sys?.batt ?? null;
              const activeComment = activeLogg?.find(
                (row) => row.systemId === sys?.id
              );

              return (
                <tr
                  key={sys?.IMEI ?? idx}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (!userIsModerator) return;

                    setMenu({
                      ...getMenuPosition(e),
                      system: sys,
                      activeComment,
                    });
                    setEditingMode(null);
                  }}
                >
                  <td className="status-td">
                    <div className="status-icon-wrapper">
                      <StatusIcon status={sys?.status} size={20} />
                    </div>
                  </td>

                  <td className="system-col">
                    <div className="nick">
                      {sys?.nick ?? "—"}
                      <span className="imei">({sys?.IMEI ?? "—"})</span>
                    </div>

                    <div
                      className={`updated ${
                        old ? "updated-old" : "updated-new"
                      }`}
                    >
                      {updated}
                    </div>

                    {sys?.comment ? (
                      <div className="system-comment">{sys.comment}</div>
                    ) : null}
                  </td>

                  <td className="lamper-col">
                    {renderLamperSixLines(sys?.lamper)}
                  </td>

                  <td className="ctrl-col">
                    {(() => {
                      const kb = sys?.kontrollboks;
                      if (kb == null) return <div className="hyphen">-</div>;
                      if (
                        typeof kb === "string" ||
                        typeof kb === "number" ||
                        typeof kb === "boolean"
                      )
                        return <div>{String(kb)}</div>;
                      if (Array.isArray(kb))
                        return kb.length ? (
                          <div className="ctrl-cell">
                            {kb.map((v, i) => (
                              <div key={i}>{renderValueSafeInline(v)}</div>
                            ))}
                          </div>
                        ) : (
                          <div className="hyphen">-</div>
                        );
                      if (typeof kb === "object")
                        return (
                          <div className="ctrl-cell">
                            {Object.entries(kb).map(([k, v]) => (
                              <div key={k}>
                                <strong>{k}:</strong> {renderValueSafeInline(v)}
                              </div>
                            ))}
                          </div>
                        );
                      return <div>{String(kb)}</div>;
                    })()}
                  </td>

                  {showBattery ? (
                    <td className="battery-col">
                      {renderBatteryCompact(batteryVal)}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div>Ingen systemer</div>
      )}

      {menu && userIsModerator && createPortal(
        <div
          className="context-menu"
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: menu.y,
            left: menu.x,
            zIndex: 1000,
            width: 300,
            maxWidth: "calc(100vw - 24px)",
          }}
        >
          <div className="context-header">
            <div className="context-title">
              {menu.system.nick ?? "—"} ({menu.system.IMEI ?? "—"})
            </div>
          </div>

          {!editingMode ? (
            <>
              {menu.activeComment ? (
                <>
                  <div className="context-item" onClick={startEdit}>
                    Endre aktiv kommentar / status
                  </div>
                  <div className="context-item" onClick={archiveActive}>
                    Arkiver aktiv kommentar
                  </div>
                  <div className="context-item" onClick={startCreate}>
                    Opprett ny og arkiver gjeldende
                  </div>
                </>
              ) : (
                <div className="context-item" onClick={startCreate}>
                  Opprett kommentar / status
                </div>
              )}
            </>
          ) : (
            <div className="context-edit">
              <label style={{ fontSize: 12 }}>Kommentar</label>
              <textarea
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                rows={3}
                style={{ width: "100%", boxSizing: "border-box" }}
                autoFocus
              />

              <label style={{ fontSize: 12, marginTop: 8 }}>
                Override status
              </label>
              <select
                value={overrideDraft}
                onChange={(e) => setOverrideDraft(e.target.value)}
                style={{
                  width: "100%",
                  borderLeft: overrideDraft
                    ? `6px solid ${getStatusColor(overrideDraft)}`
                    : "6px solid transparent",
                }}
              >
                <option value="">Ingen</option>
                <option value="AG">Grønn</option>
                <option value="AY">Gul</option>
                <option value="AR">Rød</option>
              </select>

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="btn-primary" onClick={saveComment}>
                  Lagre
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setEditingMode(null)}
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
